// Fotos: arquivo no Supabase Storage, metadado em media_assets, cópia local só como cache.
import { rpc } from './databaseService.js';
import { supabase } from './supabase.js';

export const BUCKET = 'workspace-media';
const EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/svg+xml': 'svg' };
const MAX = 10 * 1024 * 1024;

export function dataUrlToBlob(dataUrl) {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(dataUrl || '');
  if (!m) throw new Error('Imagem em formato inválido');
  const bin = m[2] ? atob(m[3]) : decodeURIComponent(m[3]);
  const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
  return new Blob([bytes], { type: m[1] });
}
export const blobToDataUrl = blob => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
export async function sha256(blob) {
  const h = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(h)].map(b => b.toString(16).padStart(2, '0')).join('');
}

function cacheDb(ws) {
  return new Promise(res => {
    const r = indexedDB.open('PMCloudCache_' + ws, 1);
    r.onupgradeneeded = () => r.result.createObjectStore('fotos');
    r.onsuccess = () => res(r.result); r.onerror = () => res(null);
  });
}
const tx = (db, mode, fn) => new Promise(res => {
  if (!db) return res(null);
  const t = db.transaction('fotos', mode); const q = fn(t.objectStore('fotos'));
  t.oncomplete = () => res(q?.result ?? null); t.onerror = () => res(null);
});

export async function uploadPhoto(ws, id, dataUrl, kind = 'photo', client = supabase) {
  const blob = dataUrlToBlob(dataUrl);
  if (!EXT[blob.type]) throw new Error('Formato não permitido: ' + blob.type);
  if (blob.size > MAX) throw new Error('Arquivo acima de 10 MB');
  const hash = await sha256(blob);
  // Caminho pelo conteúdo: a mesma imagem nunca é armazenada duas vezes.
  const path = `${ws}/${hash}.${EXT[blob.type]}`;
  const { error } = await client.storage.from(BUCKET).upload(path, blob, { contentType: blob.type, upsert: false, cacheControl: '31536000' });
  if (error && !/exist|duplicate/i.test(error.message || '') && error.statusCode !== '409') throw error;
  await rpc('register_media', { p_ws: ws, p_id: id, p_path: path, p_kind: kind, p_mime: blob.type, p_size: blob.size, p_sha: hash }, client);
  return path;
}

export function createPhotos(ws, media, onError = () => {}) {
  const paths = new Map(media.map(m => [m.id, m.storage_path]));
  const pendingKey = 'pm_pending_uploads_' + ws;
  const pending = () => JSON.parse(localStorage.getItem(pendingKey) || '[]');
  const setPending = l => localStorage.setItem(pendingKey, JSON.stringify(l));
  const dbp = cacheDb(ws);
  const inflight = new Map();

  async function sendOne(id, data) {
    const path = await uploadPhoto(ws, id, data);
    paths.set(id, path);
  }
  return {
    setMedia(list) { for (const m of list) paths.set(m.id, m.storage_path); },
    async save(id, data) {
      await tx(await dbp, 'readwrite', s => s.put(data, id));
      try { await sendOne(id, data); }
      catch (e) {
        // Foto fica no cache local e é reenviada ao reconectar; nunca é descartada.
        setPending([...new Set([...pending(), id])]); onError(e);
      }
    },
    async load(id) {
      if (!id) return null;
      const db = await dbp;
      const hit = await tx(db, 'readonly', s => s.get(id));
      if (hit) return hit;
      const path = paths.get(id);
      if (!path) return null;
      if (inflight.has(id)) return inflight.get(id);
      const p = (async () => {
        const { data, error } = await supabase.storage.from(BUCKET).download(path);
        if (error) return null;
        const url = await blobToDataUrl(data);
        await tx(db, 'readwrite', s => s.put(url, id));
        return url;
      })().finally(() => inflight.delete(id));
      inflight.set(id, p);
      return p;
    },
    // Exclusão da interface remove só a cópia local; o arquivo permanece recuperável no Storage.
    async forget(id) { await tx(await dbp, 'readwrite', s => s.delete(id)); },
    async retryPending() {
      const left = [];
      for (const id of pending()) {
        const data = await tx(await dbp, 'readonly', s => s.get(id));
        if (!data) continue;
        try { await sendOne(id, data); } catch { left.push(id); }
      }
      setPending(left);
    },
    pendingCount: () => pending().length,
  };
}
